import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import ResizeObserver from 'resize-observer-polyfill';
import { VIDEO_HEIGHT_FACTOR, VIDEO_WIDTH_FACTOR } from '../../config/video.constants';

const StyledVideoInput = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    video {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

export interface VideoInputProps extends React.VideoHTMLAttributes<HTMLVideoElement> {
    mediaStream?: MediaStream;
    resize?: boolean;
    videoRef?: React.RefObject<HTMLVideoElement | null>;
}
/**
 * Video View Container component
 */
const VideoInput: React.FC<VideoInputProps> = ({
    mediaStream,
    videoRef: _videoRef = null,
    className,
    resize = false,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);

    const calculateVideoDimension = (containerWidth: number, containerHeight: number) => {
        let computedWidth = containerWidth;
        let computedHeight = containerWidth * VIDEO_HEIGHT_FACTOR;

        if (containerHeight < computedHeight) {
            computedHeight = containerHeight;
            computedWidth = computedHeight * VIDEO_WIDTH_FACTOR;
        }
        return {
            width: computedWidth,
            height: computedHeight,
        };
    };

    useEffect(() => {
        const videoEl = _videoRef?.current || videoRef.current;
        if (videoEl) {
            videoEl.volume = 0;
            videoEl.muted = true;
            if (mediaStream) {
                videoEl.srcObject = mediaStream;
            }
        }
        return () => {
            if (videoEl) {
                videoEl.srcObject = null;
            }
        };
    }, [_videoRef, videoRef]);

    const handleResize = (entries: ResizeObserverEntry[]) => {
        // eslint-disable-next-line no-restricted-syntax
        for (const entry of entries) {
            const { width, height } = entry.contentRect;
            const videoDimension = calculateVideoDimension(width, height);
            const videoEl = _videoRef?.current || videoRef.current;
            if (videoEl) {
                videoEl.width = videoDimension.width;
                videoEl.height = videoDimension.height;
                videoEl.style.width = `${videoDimension.width}px`;
                videoEl.style.height = `${videoDimension.height}px`;
            }
        }
    };

    useEffect(() => {
        const resizeObserver = new ResizeObserver(handleResize);

        if (containerRef.current && resize) {
            resizeObserver.observe(containerRef.current);
        }

        return () => {
            if (containerRef.current && resize) {
                resizeObserver.unobserve(containerRef.current);
            }
        };
    }, [resize]);

    return (
        // eslint-disable-next-line react/jsx-no-comment-textnodes
        <StyledVideoInput ref={containerRef} className={className}>
            <video
                ref={_videoRef ?? videoRef}
                autoPlay
                muted
                onContextMenu={(event) => event.preventDefault()}
            />
        </StyledVideoInput>
    );
};

export default VideoInput;
